/**
 * `/settings/account` (#581): how the learner signs in, the interface language and the Moodle connection.
 *
 * The display name is saved with PATCH /profile (#648). The language is the same mechanism as the EN · עב switch (`useLocale().setLocale`, which saves it
 * to the profile when signed in). A launch-only session (`username === null`) has no sign-in, so it gets a sentence
 * saying so and no Sign out - signing out of a launch session would be a trap (see account-control.tsx).
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { useAuthSession, useSignOut } from '../../app/account-control.js';
import { useLocale, useMessages, type UiLocale } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';
import { MoodleSection } from './moodle-section.js';

const messages = defineMessages({
  en: {
    title: 'Account',
    intro: 'How you sign in, the name shown, the language and the Moodle connection.',
    displayName: 'Display name',
    displayNameHelp: 'The name the account menu shows. Optional.',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved.',
    signIn: 'Sign-in',
    username: 'Username',
    password: 'Password',
    passwordNote: 'Changing the password is not possible yet. Ask whoever runs DaVinci to change it.',
    launchOnly: 'This session came from the launch link and has no sign-in, so there is nothing to sign out of.',
    signOut: 'Sign out',
    signingOut: 'Signing out…',
    signOutFailed:
      'DaVinci could not sign you out — it may not be reachable. You are still signed in. Try again.',
    languageTitle: 'Interface language',
    languageHelp:
      'Saved to your account, so it is the same on every device. The EN · עב switch on every screen changes it too.',
  },
  he: {
    title: 'החשבון',
    intro: 'הכניסה ל-DaVinci, השם שמוצג, השפה והחיבור ל-Moodle.',
    displayName: 'שם לתצוגה',
    displayNameHelp: 'השם שמופיע בתפריט החשבון. לא חובה.',
    save: 'שמירה',
    saving: 'שומר…',
    saved: 'נשמר.',
    signIn: 'כניסה',
    username: 'שם משתמש',
    password: 'סיסמה',
    passwordNote: 'שינוי סיסמה עוד לא קיים. כדי לשנות, צריך לפנות למפעיל של DaVinci.',
    launchOnly: 'ההפעלה הזאת הגיעה מקישור ההפעלה ואין בה כניסה עם שם משתמש, ולכן אין ממה להתנתק.',
    signOut: 'התנתקות',
    signingOut: 'מתנתק…',
    signOutFailed: 'DaVinci לא הצליח לנתק אותך — ייתכן שהשרת אינו זמין. החיבור עדיין פעיל. כדאי לנסות שוב.',
    languageTitle: 'שפת הממשק',
    languageHelp: 'נשמרת בחשבון, כך שהיא זהה בכל מכשיר. אפשר להחליף גם מהמתג EN · עב בכל מסך.',
  },
});

const LANGUAGES: readonly { readonly locale: UiLocale; readonly name: string }[] = [
  { locale: 'en', name: 'English' },
  { locale: 'he', name: 'עברית' },
];

export function AccountPage(): React.JSX.Element {
  const t = useMessages(messages);
  const { locale, setLocale } = useLocale();
  const session = useAuthSession();
  const { signOut, isPending, failed } = useSignOut();
  const username = session.isSuccess ? session.data.username : null;

  return (
    <div className="page">
      <h2>{t.title}</h2>
      <p>{t.intro}</p>

      {session.isSuccess ? (
        <section className="settings-card" aria-labelledby="signin-heading">
          <h3 id="signin-heading">{t.signIn}</h3>
          {username === null ? (
            <p>{t.launchOnly}</p>
          ) : (
            <>
              <dl className="settings-facts">
                <dt>{t.username}</dt>
                <dd dir="ltr">
                  <strong>{username}</strong>
                </dd>
                <dt>{t.password}</dt>
                <dd>{t.passwordNote}</dd>
              </dl>
              <button type="button" className="button-neutral settings-button" onClick={signOut} disabled={isPending}>
                {isPending ? t.signingOut : t.signOut}
              </button>
              {failed ? <p role="alert">{t.signOutFailed}</p> : null}
            </>
          )}
        </section>
      ) : null}

      <DisplayNameCard />

      <section className="settings-card">
        <fieldset className="settings-radios">
          <legend>{t.languageTitle}</legend>
          <p className="field-hint">{t.languageHelp}</p>
          {LANGUAGES.map((language) => (
            <label
              key={language.locale}
              className="settings-radio"
              data-checked={locale === language.locale}
              lang={language.locale}
            >
              <input
                type="radio"
                name="ui-language"
                value={language.locale}
                checked={locale === language.locale}
                onChange={() => setLocale(language.locale)}
              />
              <span>{language.name}</span>
              <span className="settings-radio-mark" aria-hidden="true">
                {locale === language.locale ? '✓' : ''}
              </span>
            </label>
          ))}
        </fieldset>
      </section>

      <MoodleSection />
    </div>
  );
}

/** The name the account menu shows. Empty clears it (the server stores null). */
function DisplayNameCard(): React.JSX.Element {
  const t = useMessages(messages);
  const api = useApi();
  const queryClient = useQueryClient();
  const profile = useQuery({ queryKey: queryKeys.profile, queryFn: () => api.profile(), retry: false });
  const saved = profile.data?.displayName ?? '';
  const [name, setName] = useState(saved);
  useEffect(() => setName(saved), [saved]);
  const save = useMutation({
    mutationFn: (value: string) => api.updateProfile({ displayName: value === '' ? null : value }),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.profile, data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.authSession });
    },
  });
  const trimmed = name.trim();
  return (
    <section className="settings-card" aria-labelledby="display-name-heading">
      <h3 id="display-name-heading">{t.displayName}</h3>
      <form
        className="settings-inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate(trimmed);
        }}
      >
        <label htmlFor="display-name" className="settings-form-hint">
          {t.displayNameHelp}
        </label>
        <div className="settings-form-row">
          <input
            id="display-name"
            name="displayName"
            type="text"
            autoComplete="nickname"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <button type="submit" disabled={save.isPending || !profile.isSuccess}>
            {save.isPending ? t.saving : t.save}
          </button>
        </div>
      </form>
      {save.isSuccess ? <p role="status">{t.saved}</p> : null}
      {save.isError ? <FailureNotice error={save.error} /> : null}
    </section>
  );
}
